import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { bookingsApi, ApiRequestError, type CalendarEvent, FRIENDS_USER_NAME } from '@/lib/api';

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  userRole: 'owner' | 'admin' | 'user';
  onUpdate: () => void;
}

export default function EventDetailModal({
  isOpen,
  onClose,
  event,
  userRole,
  onUpdate,
}: EventDetailModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [decisionMessage, setDecisionMessage] = useState('');
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [showDecisionForm, setShowDecisionForm] = useState<'approve' | 'decline' | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    guest_name: '',
    guest_email: '',
  });

  // Reset edit form when event changes
  useEffect(() => {
    if (event) {
      setEditData({
        start_date: event.start,
        end_date: event.end,
        start_time: event.extendedProps.startTime || '12:00',
        end_time: event.extendedProps.endTime || '12:00',
        guest_name: event.extendedProps.guestName || '',
        guest_email: event.extendedProps.guestEmail || '',
      });
      setShowEditForm(false);
    }
  }, [event]);

  if (!isOpen || !event) return null;

  const { extendedProps } = event;
  const isAdminOrOwner = userRole === 'owner' || userRole === 'admin';
  const isPending = extendedProps.status === 'pending';
  const isOwnBooking = extendedProps.isOwnBooking;
  const canViewDetails = isAdminOrOwner || isOwnBooking;

  const formatDate = (dateStr: string) => {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  };

  const handleCancel = async () => {
    const reason = prompt('Please provide a reason for cancelling this booking:');
    if (!reason?.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.cancel(extendedProps.bookingId, reason.trim());
      onUpdate();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to cancel booking');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.approve(extendedProps.bookingId, decisionMessage.trim() || undefined);
      onUpdate();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to approve booking');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.decline(extendedProps.bookingId, decisionMessage.trim() || undefined);
      onUpdate();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to decline booking');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateNotes = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.updateNotes(extendedProps.bookingId, notes);
      setShowNotesForm(false);
      onUpdate();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to update notes');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to permanently delete this booking? This cannot be undone.')) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.delete(extendedProps.bookingId);
      onUpdate();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to delete booking');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await bookingsApi.edit(extendedProps.bookingId, {
        start_date: editData.start_date,
        end_date: editData.end_date,
        start_time: editData.start_time,
        end_time: editData.end_time,
        guest_name: extendedProps.isFriendsBooking ? editData.guest_name : undefined,
        guest_email: extendedProps.isFriendsBooking ? editData.guest_email : undefined,
      });
      setShowEditForm(false);
      onUpdate();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to update booking');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOwner = userRole === 'owner';
  const isApproved = extendedProps.status === 'approved';
  const canCancel = (isOwnBooking || isAdminOrOwner) && (isPending || isApproved);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {canViewDetails ? event.title : 'Reserved'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Status badge - only show for privileged users or own bookings */}
            {canViewDetails && (
              <div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isPending
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {isPending ? 'Pending Approval' : 'Approved'}
                </span>
              </div>
            )}

            {/* Edit Form (Owner only) */}
            {showEditForm && isOwner ? (
              <div className="space-y-3 p-3 bg-gray-50 rounded-md">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      value={editData.start_date}
                      onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      value={editData.end_date}
                      onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Arrival Time</label>
                    <input
                      type="time"
                      value={editData.start_time}
                      onChange={(e) => setEditData({ ...editData, start_time: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Departure Time</label>
                    <input
                      type="time"
                      value={editData.end_time}
                      onChange={(e) => setEditData({ ...editData, end_time: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                </div>
                {extendedProps.isFriendsBooking && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Guest Name</label>
                      <input
                        type="text"
                        value={editData.guest_name}
                        onChange={(e) => setEditData({ ...editData, guest_name: e.target.value })}
                        className="input mt-1"
                        placeholder="Guest name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Guest Email</label>
                      <input
                        type="email"
                        value={editData.guest_email}
                        onChange={(e) => setEditData({ ...editData, guest_email: e.target.value })}
                        className="input mt-1"
                        placeholder="guest@example.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">For arrival/departure reminders</p>
                    </div>
                  </>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleEdit}
                    disabled={isSubmitting}
                    className="btn-primary text-sm py-1"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setShowEditForm(false)}
                    className="btn-secondary text-sm py-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Date range - only show for privileged users or own bookings */}
                {canViewDetails && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-500">Dates</label>
                      {isOwner && !showEditForm && (
                        <button
                          onClick={() => setShowEditForm(true)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    <p className="text-gray-900">
                      {formatDate(event.start)} - {formatDate(event.end)}
                    </p>
                    {extendedProps.startTime && extendedProps.endTime && (
                      <p className="text-sm text-gray-500">
                        Arrival: {extendedProps.startTime} / Departure: {extendedProps.endTime}
                      </p>
                    )}
                  </div>
                )}

                {/* Simple message for standard users viewing others' bookings */}
                {!canViewDetails && (
                  <div className="text-gray-600">
                    <p>This time is already booked.</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Select different dates to make a booking request.
                    </p>
                  </div>
                )}

                {/* User info (for admins) */}
                {isAdminOrOwner && extendedProps.userName && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Guest</label>
                    <p className="text-gray-900">
                      {extendedProps.isFriendsBooking && extendedProps.guestName
                        ? `${FRIENDS_USER_NAME}: ${extendedProps.guestName}`
                        : extendedProps.userName}
                    </p>
                    {extendedProps.isFriendsBooking && (
                      <>
                        <p className="text-sm text-gray-500">(Non-member booking)</p>
                        {extendedProps.guestEmail && (
                          <p className="text-sm text-gray-600">{extendedProps.guestEmail}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {/* User message - only show for privileged users or own bookings */}
            {canViewDetails && extendedProps.userMessage && (
              <div>
                <label className="text-sm font-medium text-gray-500">Message</label>
                <p className="text-gray-900">{extendedProps.userMessage}</p>
              </div>
            )}

            {/* Owner notes (admin only) */}
            {isAdminOrOwner && (
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-500">Admin Notes</label>
                  {!showNotesForm && (
                    <button
                      onClick={() => {
                        setNotes(extendedProps.ownerNotes || '');
                        setShowNotesForm(true);
                      }}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      {extendedProps.ownerNotes ? 'Edit' : 'Add'}
                    </button>
                  )}
                </div>
                {showNotesForm ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="input min-h-[60px]"
                      placeholder="Add private notes..."
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateNotes}
                        disabled={isSubmitting}
                        className="btn-primary text-sm py-1"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setShowNotesForm(false)}
                        className="btn-secondary text-sm py-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-900">
                    {extendedProps.ownerNotes || <span className="text-gray-400 italic">No notes</span>}
                  </p>
                )}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Decision form */}
            {showDecisionForm && (
              <div className="p-3 bg-gray-50 rounded-md space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Message to user (optional)
                </label>
                <textarea
                  value={decisionMessage}
                  onChange={(e) => setDecisionMessage(e.target.value)}
                  className="input min-h-[60px]"
                  placeholder={
                    showDecisionForm === 'approve'
                      ? 'e.g., Looking forward to your stay!'
                      : 'e.g., Sorry, those dates are not available...'
                  }
                />
                <div className="flex gap-2">
                  <button
                    onClick={showDecisionForm === 'approve' ? handleApprove : handleDecline}
                    disabled={isSubmitting}
                    className={showDecisionForm === 'approve' ? 'btn-success text-sm py-1' : 'btn-danger text-sm py-1'}
                  >
                    {isSubmitting ? 'Processing...' : showDecisionForm === 'approve' ? 'Confirm Approve' : 'Confirm Decline'}
                  </button>
                  <button
                    onClick={() => setShowDecisionForm(null)}
                    className="btn-secondary text-sm py-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            {!showDecisionForm && !showEditForm && (
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {/* Cancel: own bookings, or any booking for admins/owners */}
                {canCancel && (
                  <button
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="btn-secondary text-sm"
                  >
                    {isPending ? 'Cancel Request' : 'Cancel Booking'}
                  </button>
                )}

                {/* Admin/Owner actions for pending bookings */}
                {isAdminOrOwner && isPending && !isOwnBooking && (
                  <>
                    <button
                      onClick={() => setShowDecisionForm('approve')}
                      disabled={isSubmitting}
                      className="btn-success text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setShowDecisionForm('decline')}
                      disabled={isSubmitting}
                      className="btn-danger text-sm"
                    >
                      Decline
                    </button>
                  </>
                )}

                {/* Admin/Owner can delete any booking */}
                {isAdminOrOwner && (
                  <button
                    onClick={handleDelete}
                    disabled={isSubmitting}
                    className="btn-secondary text-sm text-red-600 hover:text-red-700"
                  >
                    Delete
                  </button>
                )}

                <button onClick={onClose} className="btn-secondary text-sm ml-auto">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
